<template>
    <div ref="elementoAlvo" class="fixed bottom-25 right-6 z-300 flex flex-col items-end">

        <Transition name="slide-up">
            <div v-if="estaAberto"
                class="mb-[15px] w-[260px] max-w-[90vw] right-60 bg-white rounded-lg shadow-[0_4px_15px_rgba(0,0,0,0.2)] overflow-hidden flex flex-col"
                @keydown.stop>

                <div v-if="telaAtual === 'menu'" class="p-5 flex flex-col gap-3">
                    <div class="mb-1">
                        <h4 class="text-lg font-bold text-gray-800">Como podemos ajudar?</h4>
                        <p class="text-xs text-gray-800">Escolha uma das opções abaixo ou acesse o
                            <a href="https://suporte.ead.ifmg.edu.br/hc/mais-ifmg/pt_BR" target="_blank"
                                class="text-[#177137] font-semibold underline">
                                Suporte EaD do IFMG
                            </a>
                        </p>
                    </div>

                    <UButton to="https://suporte.ead.ifmg.edu.br/hc/mais-ifmg/pt_BR" target="_blank" variant="ghost"
                        class="w-full text-sm justify-start p-3 border border-gray-200 hover:border-[#177137] hover:bg-gray-100 text-gray-800 rounded-lg transition-all">
                        <template #leading>
                            <UIcon name="i-ph-question" class="w-6 h-6 text-[#177137]" />
                        </template>
                        Central de Ajuda
                    </UButton>

                    <UButton variant="ghost"
                        class="w-full text-sm justify-start p-3 border border-gray-200 hover:border-[#177137] hover:bg-gray-100 text-gray-800 rounded-lg transition-all hover:cursor-pointer"
                        @click="telaAtual = 'formulario'">
                        <template #leading>
                            <UIcon name="i-ph-envelope-simple" class="w-6 h-6 text-[#177137]" />
                        </template>
                        Suporte Técnico
                    </UButton>
                </div>

                <template v-else>
                    <div class="bg-[#177137] p-[15px] flex items-center justify-between">
                        <div class="flex items-center gap-2">
                            <UIcon name="i-ph-envelope-simple" class="text-white w-6 h-6" />
                            <h4 class="text-white text-md">Suporte Técnico</h4>
                        </div>
                        <UButton variant="ghost" icon="i-ph-arrow-left"
                            class="text-white hover:bg-[#125a2b] hover:cursor-pointer" @click="telaAtual = 'menu'" />
                    </div>

                    <UForm v-if="telaAtual === 'formulario'" class="p-5 flex flex-col gap-3 text-left">
                        <label class="font-semibold text-sm -mb-2">Nome:</label>
                        <UInput v-model="dadosFormulario.nome" placeholder="Seu nome completo" class="w-full" />

                        <label class="font-semibold text-sm -mb-2">E-mail:</label>
                        <UInput v-model="dadosFormulario.email" type="email" placeholder="Seu endereço de e-mail"
                            class="w-full" />

                        <label class="font-semibold text-sm -mb-2">Assunto:</label>
                        <UInput v-model="dadosFormulario.assunto" placeholder="Resuma o assunto" class="w-full" />

                        <label class="font-semibold text-sm -mb-2">Digite sua mensagem:</label>
                        <UTextarea v-model="dadosFormulario.mensagem" :rows="4" class="w-full"
                            placeholder="Descreva sua dúvida ou solicitação..." />

                        <UButton block
                            class="bg-[#177137] hover:bg-[#175a21] hover:cursor-pointer text-white font-bold py-2.5 rounded-sm transition-colors mt-2"
                            :loading="carregando" @click="enviarFormulario">
                            Enviar Mensagem
                        </UButton>
                    </UForm>

                    <div v-if="telaAtual === 'feedback'"
                        class="p-[30px_20px] flex flex-col items-center text-center gap-[15px]">
                        <UIcon
                            :name="statusFeedback === 'sucesso' ? 'i-ph-check-circle-fill' : 'i-ph-warning-circle-fill'"
                            class="w-12 h-12"
                            :class="statusFeedback === 'sucesso' ? 'text-[#177137]' : 'text-[#d32f2f]'" />
                        <h4 class="text-[1.2rem] font-bold text-[#333]">{{ conteudoFeedback.titulo }}</h4>
                        <p class="text-[0.9rem] text-[#666] leading-[1.5]">{{ conteudoFeedback.texto }}</p>
                        <UButton block class="bg-[#177137] hover:bg-[#175a21] text-white font-bold py-2.5 rounded-sm"
                            @click="voltarAoMenu">
                            Voltar para o Menu
                        </UButton>
                    </div>
                </template>
            </div>
        </Transition>

        <UButton @click="alternarWidget"
            class="w-[60px] h-[60px] rounded-full shadow-[0_4px_15px_rgba(0,0,0,0.2)] flex justify-center items-center p-0 transition-all hover:scale-105 hover:bg-[#0B4821] active:bg-[#0B4821] hover:cursor-pointer"
            :class="estaAberto ? 'bg-[#177137] text-white' : 'bg-[#177137] text-white'">
            <UIcon :name="estaAberto ? 'i-ph-x' : 'i-ph-question-mark'" class="w-[30px] h-[30px]" />
        </UButton>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'

// Estados do Widget
const estaAberto = ref(false)
const telaAtual = ref<'menu' | 'formulario' | 'feedback'>('menu')
const carregando = ref(false)
const statusFeedback = ref<'sucesso' | 'erro'>('sucesso')
const elementoAlvo = ref(null)

// Dados do formulário reativos
const dadosFormulario = reactive({
    nome: '',
    email: '',
    assunto: '',
    mensagem: ''
})

// Fecha o widget ao clicar fora
onClickOutside(elementoAlvo, () => {
    estaAberto.value = false
})

// Função para abrir/fechar
const alternarWidget = () => {
    estaAberto.value = !estaAberto.value
    if (!estaAberto.value) {
        // Pequeno atraso para resetar a tela após a animação de fechar
        setTimeout(() => { telaAtual.value = 'menu' }, 300)
    }
}

// Reseta o estado do formulário
const voltarAoMenu = () => {
    telaAtual.value = 'menu'
    dadosFormulario.nome = ''
    dadosFormulario.email = ''
    dadosFormulario.assunto = ''
    dadosFormulario.mensagem = ''
}

// Computada para definir textos de feedback
const conteudoFeedback = computed(() => {
    return statusFeedback.value === 'sucesso'
        ? { titulo: 'Mensagem enviada', texto: 'Sua mensagem foi enviada para o suporte técnico com sucesso.' }
        : { titulo: 'Erro ao enviar', texto: 'Não conseguimos processar o envio agora.' }
})

// Lógica de envio
const enviarFormulario = async () => {
    const { nome, email, assunto, mensagem } = dadosFormulario
    if (!nome || !email || !assunto || !mensagem) return

    carregando.value = true
    try {
        const resposta = await fetch('https://suporte.ead.ifmg.edu.br/n8n/webhook/processa_form_contato_suporte_mais_ifmg', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dadosFormulario)
        })
        statusFeedback.value = resposta.ok ? 'sucesso' : 'erro'
    } catch {
        statusFeedback.value = 'erro'
    } finally {
        carregando.value = false
        telaAtual.value = 'feedback'
    }
}
</script>
<style scoped>
/* Animação slide-up */
.slide-up-enter-active {
    animation: slideUp 0.3s ease-out;
    transform-origin: bottom right;
}

.slide-up-leave-active {
    animation: slideUp 0.2s ease-in reverse;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.9);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@media (max-width: 350px) {
    .w-\[320px\] {
        width: 280px;
    }
}
</style>